/**
 * Toast Component
 * 
 * Customized toast notifications using React Hot Toast
 * with Alianzo design system styling
 * 
 * @component
 * @example
 * ```tsx
 * import { Toaster } from '@/components/Common/Toast';
 * 
 * function App() {
 *   return (
 *     <>
 *       <Toaster />
 *       <YourApp />
 *     </>
 *   );
 * }
 * ```
 */

import { FC } from 'react';
import { Toaster as HotToaster, ToastBar, toast as hotToast } from 'react-hot-toast';
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiInfo, FiX, FiLoader } from 'react-icons/fi';
import styles from './Toast.module.css';

/**
 * Toaster Component
 * Renders the toast container
 */
export const Toaster: FC = () => {
  return (
    <HotToaster
      position="top-right"
      reverseOrder={false}
      gutter={12}
      containerClassName={styles.toastContainer}
      toastOptions={{
        duration: 4000,
        style: {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      {(t) => (
        <ToastBar toast={t}>
          {({ message }) => (
            <div className={`${styles.toast} ${getToastVariantClass(t.type)}`}>
              <div className={styles.toastIcon}>
                {getToastIcon(t.type)}
              </div>
              <div className={styles.toastContent}>
                {message}
              </div>
              {t.type !== 'loading' && (
                <button
                  className={styles.toastClose}
                  onClick={() => hotToast.dismiss(t.id)}
                  aria-label="Fechar notificação"
                >
                  <FiX size={16} />
                </button>
              )}
            </div>
          )}
        </ToastBar>
      )}
    </HotToaster>
  );
};

/**
 * Get toast icon based on type
 */
const getToastIcon = (type: string) => {
  const iconProps = { size: 20 };

  switch (type) {
    case 'success':
      return <FiCheckCircle {...iconProps} />;
    case 'error':
      return <FiXCircle {...iconProps} />;
    case 'warning':
      return <FiAlertTriangle {...iconProps} />;
    case 'loading':
      return <FiLoader {...iconProps} className={styles.spinner} />;
    case 'custom':
    default:
      return <FiInfo {...iconProps} />;
  }
};

/**
 * Get toast variant class
 */
const getToastVariantClass = (type: string): string => {
  switch (type) {
    case 'success':
      return styles.toastSuccess;
    case 'error':
      return styles.toastError;
    case 'loading':
      return styles.toastLoading;
    case 'custom':
      return styles.toastInfo;
    default:
      return styles.toastInfo;
  }
};

export default Toaster;
